import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone, Mail, Gift } from "lucide-react";
import { openReferral } from "@/components/site/ReferralPopup";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/api";
import { PORTALS } from "@/lib/portals";
import { Logo } from "@/components/site/Logo";
import { useQuote } from "@/components/site/QuoteContext";
import { SignInMenu, PortalLink } from "@/components/site/SignInMenu";
import { NavDropdown, MobileMenuGroup } from "@/components/site/NavDropdown";
import { NAV_MENUS } from "@/lib/navMenus";

export const QuoteButton = ({ children = "Get a free quote", className = "", testid = "nav-quote-btn", dark = false }) => {
  const { open } = useQuote();
  return (
    <button
      data-testid={testid}
      onClick={open}
      className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 active:scale-95 ${dark ? "bg-[#0B2A6F] text-white hover:bg-[#050B1F]" : "bg-lime-400 text-[#050B1F] hover:bg-lime-300 hover:shadow-[0_10px_40px_-10px_rgba(56,189,248,0.8)]"} ${className}`}
    >
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
    </button>
  );
};

const TopBar = () => (
  <div className="bg-[#050B1F] text-white" data-testid="nav-topbar">
    <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-1.5 text-xs sm:px-6 lg:px-12">
      <div className="flex items-center gap-5">
        <a href={PHONE_HREF} data-testid="topbar-phone-link" className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold hover:text-lime-300"><Phone className="h-3.5 w-3.5 text-lime-300" /> <span className="hidden min-[400px]:inline">{PHONE}</span><span className="min-[400px]:hidden">Call</span></a>
        <a href={`mailto:${EMAIL}`} data-testid="topbar-email-link" className="hidden items-center gap-1.5 hover:text-lime-300 sm:inline-flex"><Mail className="h-3.5 w-3.5 text-lime-300" /> {EMAIL}</a>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={openReferral} data-testid="topbar-referral-btn" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-lime-400 px-3 py-0.5 font-bold text-[#050B1F] transition-colors hover:bg-lime-300">
          <Gift className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Referred? Get a</span> $250 bonus
        </button>
        <div className="whitespace-nowrap lg:hidden"><SignInMenu variant="dark" testid="topbar-signin-btn" /></div>
      </div>
    </div>
  </div>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <TopBar />
      <div className={`border-b bg-white/95 backdrop-blur-xl transition-[box-shadow,border-color] duration-500 ${scrolled ? "border-[#0B2A6F]/10 shadow-[0_10px_40px_-20px_rgba(5,11,31,0.35)]" : "border-transparent"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2.5 sm:px-6 lg:px-12">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_MENUS.map((m) => <NavDropdown key={m.to} menu={m} />)}
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden lg:block"><SignInMenu testid="nav-signin-btn" /></div>
            <QuoteButton dark className="hidden sm:inline-flex !py-2.5">Get Started</QuoteButton>
            <button data-testid="nav-mobile-toggle" onClick={() => setMobile((v) => !v)} className="rounded-full bg-[#EAF3FF] p-2.5 text-[#0B2A6F] md:hidden" aria-label="Menu">
              {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="mx-3 mt-2 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-3xl border border-[#0B2A6F]/10 bg-white p-4 shadow-2xl md:hidden" data-testid="nav-mobile-menu" data-lenis-prevent>
            {NAV_MENUS.map((m) => <MobileMenuGroup key={m.to} menu={m} onNavigate={() => setMobile(false)} />)}
            <p className="mt-3 px-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Sign in</p>
            {PORTALS.map((p) => <PortalLink key={p.id} p={p} testid={`mobile-signin-${p.id}-link`} onClick={() => setMobile(false)} />)}
            <QuoteButton testid="mobile-quote-btn" className="mt-3 w-full justify-center">Get Started</QuoteButton>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
